// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import { getProp } from "@insite/mobius/utilities";
import injectCss from "@insite/mobius/utilities/injectCss";
import { ArrowPosition, ArrowType } from "@west/App/Components/XngCarousel";
import {
    arrowNavigationIcons,
    getArrowPosition,
    useArrowNavigation,
} from "@west/App/Components/XngCarousel/arrowNavigation.helper";
import { color, xClassNames } from "@west/XDL/Utilities";
import React, { FC, useCallback } from "react";
import styled, { css } from "styled-components";
import { useSwiper } from "swiper/react";
import translate from "@insite/client-framework/Translate";

export interface ArrowNavigationProps {
    arrowStyle: "default" | "mobile";
    arrowType: ArrowType;
    showPaginationContainer?: boolean;
}

const ArrowNavigationClickable = styled(Clickable)<{
    position: ArrowPosition;
    showPaginationContainer?: ArrowNavigationProps["showPaginationContainer"];
    arrowStyle: ArrowNavigationProps["arrowStyle"];
}>`
    ${injectCss}
`;

export interface ArrowNavigationStyles {
    clickable?: ClickablePresentationProps;
    defaultIcon?: IconPresentationProps;
    mobileIcon?: IconPresentationProps;
}

export const arrowNavigationStyles: ArrowNavigationStyles = {
    clickable: {
        css: css`
            ${({ arrowStyle, showPaginationContainer, position }: any) => {
                if (arrowStyle === "default") {
                    return css`
                        position: absolute;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        top: 50%;
                        transform: translateY(-50%);
                        z-index: 10;
                        margin-top: -19px; // exclude xng-pagination-container
                        transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;

                        ${showPaginationContainer &&
                        css`
                            margin-top: -19px; // exclude xng-pagination-container
                        `}

                        ${() => {
                            if (position === "left") {
                                return css`
                                    left: 0;
                                    right: auto;
                                `;
                            }
                            if (position === "right") {
                                return css`
                                    left: auto;
                                    right: 0;
                                `;
                            }
                            return null;
                        }}

                        height: 66px;
                        width: 32px;
                        background: ${color("common.ui6", 0.66)};

                        &:hover {
                            background: ${color("common.ui9")};
                        }
                        &.swiper-button-disabled {
                            display: none;
                        }
                    `;
                }
                if (arrowStyle === "mobile") {
                    return css`
                        background: transparent;
                        position: relative;
                        left: initial;
                        top: initial;
                        transform: initial;
                        width: auto;
                        height: auto;
                        &.swiper-button-disabled {
                            opacity: 0.33;
                        }
                    `;
                }
            }}

            &:focus {
                outline: none;
            }
        `,
    },
    defaultIcon: {
        color: "common.white",
        css: css`
            &,
            & > svg {
                height: 26px;
                width: 16px;
            }
        `,
    },
    mobileIcon: {
        color: "common.ui6",
        css: css`
            &,
            & > svg {
                height: 20px;
                width: 12px;
            }
        `,
    },
};

const styles = arrowNavigationStyles;
const ArrowNavigation: FC<ArrowNavigationProps> = React.memo(({ arrowType, arrowStyle }) => {
    const swiperInstance = useSwiper();
    const arrowPosition = getArrowPosition(arrowType, swiperInstance?.isHorizontal() ? "horizontal" : "vertical");
    const iconProps = arrowNavigationIcons[arrowPosition];

    const {
        className,
        element: arrowElement,
        disabled,
    } = useArrowNavigation({
        swiper: swiperInstance,
        arrowType,
    });

    const onClickHandler = useCallback(
        (event: any) => {
            arrowElement?.click();
        },
        [arrowElement],
    );

    if (!swiperInstance || !arrowElement) {
        return null;
    }

    return (
        <ArrowNavigationClickable
            {...styles.clickable}
            position={arrowPosition}
            className={xClassNames.append([
                `xng-swiper-button-${arrowType}`,
                `xng-swiper-position-${arrowPosition}`,
                className,
            ])}
            aria-label={translate(`${arrowType} slide`)}
            onClick={onClickHandler}
            disabled={disabled}
            arrowStyle={arrowStyle}
        >
            <Icon
                src={iconProps.src}
                size={iconProps.size}
                {...(arrowStyle === "mobile" ? styles.mobileIcon : styles.defaultIcon)}
            />
        </ArrowNavigationClickable>
    );
});

export default ArrowNavigation;
